/*
    Process and route messages in several ways:
        1. store them into the storage
        2. display at the screen
        3. send to web push notification channel when document or window is minimized or hidden
 */
export const SM_TYPE_INAPP_NOTICE      = 1;  //inapp system notification
export const SM_TYPE_CLIENT_NOTICE     = 2;  //notification where sender and recipient is defined
export const SM_TYPE_CHAT_MESSAGE      = 3;  //chat message where sender and recipient is defined

class UIServiceMessenger {
    /*
       silentMode: suppress all messages output
       defaultContainer: DOM element used to display messages with default (undefined) channel type
       webPushSender: callback function with two args (title, text) used to display messages through pushNotificationAPI
     */
    constructor(silentMode, defaultContainer, webPushSender) {
        this.silent = silentMode;
        this.channels = new Map();
        this.channels.set("default", defaultContainer ? defaultContainer : document.body);
        this.elog = (typeof elog === "function") ? elog : function (severity, message) {
            if (typeof console[severity.toLowerCase()] === "function") {
                console[severity.toLowerCase()](message);
            }
        };
        this.pushNotificationToSW = webPushSender;
        this.storageWriter = null;
        this.display = null;

        this.SM_TYPE_INAPP_NOTICE = SM_TYPE_INAPP_NOTICE
        this.SM_TYPE_CLIENT_NOTICE = SM_TYPE_CLIENT_NOTICE
        this.SM_TYPE_CHAT_MESSAGE = SM_TYPE_CHAT_MESSAGE
        //backward compatibility with legacy integration to mylocator app
        if (typeof GeoContext == "function") {
            this.SM_TYPE_INAPP_NOTICE = CM_TYPE_INAPP_NOTICE
            this.SM_TYPE_CLIENT_NOTICE = CM_TYPE_CLIENT_NOTICE
            this.SM_TYPE_CHAT_MESSAGE = CM_TYPE_CHAT_MESSAGE
        }
    }

    /*
       store message into a storage and display
       message - object
       [message.timestamp] - current timestamp, can be null
       [message.uid]     - unified message ID, can be null
       [message.type]    - see messageTypes.js
       [message.payload] - depends on message.type
    */
    throwMessage(message) {
        if (typeof this.storageWriter === "function") {
            if (message.payload.fleeting) {
                this._writeToDisplay(message);
                return;
            }
            this.storageWriter(message).then( function ( message ) {
                this._writeToDisplay(message);
            }.bind(this)).catch(function (err) {
                elog("ERROR", "Can't store a message: " + err.message);
                this._writeToDisplay(message);
            }.bind(this))
        } else {
            if (! message.payload.fleeting) {
                elog("WARN", "Storage writer isn't set to serviceMessenger");
            }
            this._writeToDisplay(message);
        }
    }

    //push the message
    //returns message (with filled uid and timestamp)
    message(message) {
        if (! message.uid )
            message.uid = this.uuidv4();

        if (! message.timestamp)
            message.timestamp = new Date().getTime();

        if (message.type === this.SM_TYPE_CHAT_MESSAGE) {
            //try to deliver message via webpush notifications if app windows is minimized and webpush function is defined
            if (document.hidden && typeof webPushSendControlMessageToSW === "function")
                webPushSendControlMessageToSW(message);
        }
        this.throwMessage(message);

        return message;
    }

    replaceMessage(message, uid) {
        if (uid == null)
            return;
        this._writeToDisplay(message, uid)
    }
    /*
    * store and display a notification message
    * object - a list (or single object) used for decoration and dysplay control
    *   features:
    *   - decorate some text's substrings with a hyperlink, the object must have fields: name and uid, so
    *     object.uid is searched in the text and is replaced with "name" and a hyperlink to this object
    *   - colorize the text at all, the object must have the field "color"
    *   - throw 'draw attention' event (usually it leds to appear the message window on the front),
    *     the object must have field "attention"
    *   - suppress the same repeated messages, object must have the field "suppress"
    *   - do not store the message in persistent storage, object must have the field "fleeting"
    *
    * returns message object
    * */
    noticeMessage(text, object, url) {
        let objects = [];
        let payload = {
            text: text,
            object: [],
            url: url
        };
        if (object) {
            ((object instanceof Array) ? object : [object]).forEach(function (o) {
                payload.object.push({name: o.name, uid: o.gouid || o.uid || o.uuid})
                if ('attention' in o) {
                    payload.attention = true;
                }
                if ('suppress' in o) {
                    payload.suppress_the_same = true;
                }
                if ('fleeting' in o) {
                    payload.fleeting = true;
                }
                if ('color' in o) {
                    payload.color = o.color;
                }
            });
        }
        payload.object = objects

        return this.message({type: this.SM_TYPE_INAPP_NOTICE, payload: payload})
    }
    /*
        throw text message to
        push - throw push notification message while the application is invisible
        LEGACY.
        writes a message to the main screen like falling message flow at a right-top corner
     */
    notice(text, channel, push) {
        // the document tab or window is hidden or inactive, fire the push notification message
        if (push && document.hidden && this.pushNotificationToSW) {
            this.pushNotificationToSW('Locator', text);
            return
        }

        if (this.silent)
            return;

        /* where to out the notice message */
        let container = channel ? this.channels.get(channel) : this.channels.get("default");
        if (! container) {
            this.elog("WARN", "UIServiceMessenger: unknown output channel " + channel);
            return
        }

        let div = document.createElement('div');
        div.innerText = text;
        div.classList.add("serviceMessenger-fallingMessage");
        container.append(div);

        div.addEventListener("animationend", function (obj) {obj.target.remove();}, false);
    }

    serverError(code, message) {
        if (code === 17) {
            message = "Please sign in to get access to your workspace otherwise your work won't be saved"
        }
        if (message == null) {
            message = "Sorry, the server is in maintenance and some services could be unavailable now";
        }
        this.noticeMessage(message, { attention: true, suppress: true, fleeting: true, color: "red"})
    }

    setStorageWriter(cb) {
        this.storageWriter = cb;
    }

    //display must have two methods AddMessage and ReplaceMessage
    setDisplay(display) {
        if (display)
            this.display = display;
    }

    //simple uid generator, it's enough to our purpose
    uuidv4() {
        return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
            var r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
            return v.toString(16);
        });
    }

    /*
        add message to display or replace the message if uid is defined
     */
    _writeToDisplay(message, uid) {
        if (!this.display)
            return;
        //console.log(`${uid} ${message.uid} ${message.payload.text}`)
        if (uid != null && this.display.ReplaceMessage) {
            if (this.display.ReplaceMessage(uid, message))
                return
        }
        if (this.display.AddMessage)
            this.display.AddMessage(message);
    }

}

export { UIServiceMessenger };
